import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, API, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { StatoVerbaleBadge } from "@/components/StatoBadge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, MapPin, Paperclip, X, Pencil, Trash2, ShieldCheck, Lock, Users, Undo2, FileDown } from "lucide-react";
import { toast } from "sonner";
import { TIPI_INTERVENTO, STATI_VERBALE, fmtDateTime } from "@/lib/constants";
import { GuardiePicker, AllegatiDialog } from "@/components/InterventiParts";
import { RegistroMensile } from "@/components/RegistroMensile";
import { secureClick } from "@/lib/openFile";

const EMPTY_FORM = {
  data_ora: "", comune: "", indirizzo: "", tipo: "Maltrattamento",
  descrizione: "", stato: "In corso", guardie_ids: [], allegati: [],
};

export default function Interventi() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const isStaff = user?.role !== "guardia";
  const isGuardia = user?.role === "guardia";
  const myGid = user?.guardia_id;
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delId, setDelId] = useState(null);
  const [allegatiOf, setAllegatiOf] = useState(null);
  const [fTipo, setFTipo] = useState("tutti");
  const [form, setForm] = useState(EMPTY_FORM);

  const { data: interventi = [] } = useQuery({
    queryKey: ["interventi", fTipo],
    queryFn: async () => (await api.get(`/interventi${fTipo !== "tutti" ? `?tipo=${encodeURIComponent(fTipo)}` : ""}`)).data,
  });

  const { data: guardie = [] } = useQuery({
    queryKey: ["guardie-nomi"],
    queryFn: async () => (await api.get("/guardie-nomi")).data,
  });

  const addFiles = (files) => {
    Array.from(files).slice(0, 4).forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => setForm((f) => ({ ...f, allegati: [...f.allegati, { name: file.name, type: file.type, data: reader.result }] }));
      reader.readAsDataURL(file);
    });
  };

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM, guardie_ids: isGuardia && myGid ? [myGid] : [] });
    setOpen(true);
  };

  const openEdit = (i) => {
    setEditId(i.id);
    setForm({
      data_ora: i.data_ora || "", comune: i.comune || "", indirizzo: i.indirizzo || "",
      tipo: i.tipo || "Maltrattamento", descrizione: i.descrizione || "", stato: i.stato || "In corso",
      guardie_ids: i.guardie_ids || [], allegati: i.allegati || [],
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.data_ora || !form.comune) { toast.error("Data/ora e comune sono obbligatori"); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      if (editId) {
        await api.patch(`/interventi/${editId}`, payload);
        toast.success("Verbale aggiornato");
      } else {
        await api.post("/interventi", payload);
        toast.success("Verbale registrato");
      }
      qc.invalidateQueries({ queryKey: ["interventi"] });
      setOpen(false);
      setEditId(null);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const updateStato = async (id, stato) => {
    try {
      await api.patch(`/interventi/${id}`, { stato });
      qc.invalidateQueries({ queryKey: ["interventi"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const setVerificato = async (id, verificato) => {
    try {
      await api.post(`/interventi/${id}/verifica`, { verificato });
      toast.success(verificato ? "Intervento verificato" : "Verifica annullata");
      qc.invalidateQueries({ queryKey: ["interventi"] });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/interventi/${delId}`);
      toast.success("Verbale eliminato");
      qc.invalidateQueries({ queryKey: ["interventi"] });
      setDelId(null);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div className="gz-fade-up">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl">Registro verbali</h1>
          <p className="mt-1 text-sm text-slate-500">{isStaff ? "Tutti gli interventi del corpo." : "I tuoi interventi e verbali."}</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={fTipo} onValueChange={setFTipo}>
            <SelectTrigger className="w-[180px]" data-testid="filter-tipo-verbale"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="tutti">Tutti i tipi</SelectItem>
              {TIPI_INTERVENTO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button className="bg-[#14382C] hover:bg-[#0E281F]" data-testid="new-verbale-button" onClick={openNew}>
            <Plus className="mr-2 h-4 w-4" /> Nuovo verbale
          </Button>
        </div>
      </div>

      {isStaff && <RegistroMensile />}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto gz-scroll">
          <DialogHeader><DialogTitle>{editId ? "Modifica verbale" : "Nuovo verbale di intervento"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Data e ora</Label>
                <Input type="datetime-local" data-testid="verbale-dataora" value={form.data_ora} onChange={(e) => setForm({ ...form, data_ora: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Tipo segnalazione</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger data-testid="verbale-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>{TIPI_INTERVENTO.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Comune</Label><Input data-testid="verbale-comune" value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} /></div>
              <div className="space-y-2"><Label>Indirizzo</Label><Input data-testid="verbale-indirizzo" value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} /></div>
            </div>
            <div className="space-y-2">
              <Label>Guardie operanti</Label>
              <GuardiePicker guardie={guardie} value={form.guardie_ids} onChange={(v) => setForm({ ...form, guardie_ids: v })} lockedId={isGuardia ? myGid : null} />
            </div>
            <div className="space-y-2">
              <Label>Descrizione</Label>
              <Textarea rows={3} data-testid="verbale-descrizione" value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Stato</Label>
              <Select value={form.stato} onValueChange={(v) => setForm({ ...form, stato: v })}>
                <SelectTrigger data-testid="verbale-stato"><SelectValue /></SelectTrigger>
                <SelectContent>{STATI_VERBALE.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Allegati (foto/PDF)</Label>
              <Input type="file" multiple accept="image/*,.pdf" data-testid="verbale-allegati" onChange={(e) => addFiles(e.target.files)} />
              {form.allegati.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {form.allegati.map((a, i) => (
                    <span key={i} className="inline-flex items-center gap-1 rounded bg-slate-100 px-2 py-1 text-xs">
                      <Paperclip className="h-3 w-3" /> {a.name}
                      <button onClick={() => setForm((f) => ({ ...f, allegati: f.allegati.filter((_, j) => j !== i) }))}><X className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={submit} disabled={saving} data-testid="save-verbale-button" className="bg-[#14382C] hover:bg-[#0E281F]">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {editId ? "Salva modifiche" : "Registra verbale"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(delId)} onOpenChange={(v) => !v && setDelId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Elimina verbale</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">Confermi l'eliminazione del verbale? L'operazione è irreversibile.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelId(null)}>Annulla</Button>
            <Button onClick={remove} data-testid="confirm-delete-verbale" className="bg-red-600 hover:bg-red-700">Elimina definitivamente</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AllegatiDialog intervento={allegatiOf} onClose={() => setAllegatiOf(null)} />

      {interventi.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-12 text-center text-sm text-slate-400">Nessun verbale registrato.</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {interventi.map((i) => (
            <Card key={i.id} data-testid={`verbale-${i.id}`} className="border-slate-200 shadow-sm transition-shadow hover:shadow-md">
              <CardContent className="p-5">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono-code text-xs font-semibold text-[#166534]">{i.protocollo}</span>
                      <span className="text-xs text-slate-400">{fmtDateTime(i.data_ora)}</span>
                      {i.verificato ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800" data-testid={`verbale-verificato-${i.id}`}>
                          <ShieldCheck className="h-3 w-3" /> Verificato{i.verificato_da ? ` da ${i.verificato_da}` : ""}
                        </span>
                      ) : (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800" data-testid={`verbale-da-verificare-${i.id}`}>Da verificare</span>
                      )}
                    </div>
                    <p className="mt-1 font-heading font-bold text-slate-900">{i.tipo}</p>
                    <p className="mt-1 flex items-center gap-1 text-sm text-slate-600"><MapPin className="h-3.5 w-3.5" /> {i.comune}{i.indirizzo ? `, ${i.indirizzo}` : ""}</p>
                    {i.descrizione && <p className="mt-2 whitespace-pre-line text-sm text-slate-500">{i.descrizione}</p>}
                    <p className="mt-2 flex items-start gap-1 text-xs text-slate-500" data-testid={`verbale-guardie-${i.id}`}><Users className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {i.guardie_nomi?.length ? i.guardie_nomi.join(", ") : "—"}</p>
                    {i.allegati?.length > 0 && (
                      <Button variant="outline" size="sm" className="mt-3" onClick={() => setAllegatiOf(i)} data-testid={`allegati-verbale-${i.id}`}>
                        <Paperclip className="mr-1.5 h-3.5 w-3.5" /> Visualizza allegati ({i.allegati.length})
                      </Button>
                    )}
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    {i.can_edit ? (
                      <Select value={i.stato} onValueChange={(v) => updateStato(i.id, v)}>
                        <SelectTrigger className="w-[210px]" data-testid={`verbale-stato-${i.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{STATI_VERBALE.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : (
                      <StatoVerbaleBadge stato={i.stato} />
                    )}
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {i.verificato && (
                        <Button variant="outline" size="sm" asChild>
                          <a href={`${API}/interventi/${i.id}/pdf`} onClick={secureClick(`${API}/interventi/${i.id}/pdf`)} target="_blank" rel="noopener noreferrer" data-testid={`pdf-verbale-${i.id}`}>
                            <FileDown className="mr-1.5 h-3.5 w-3.5 text-red-700" /> Riepilogo PDF
                          </a>
                        </Button>
                      )}
                      {i.can_verify && (
                        <Button size="sm" className="bg-[#166534] hover:bg-[#14532d]" data-testid={`verifica-verbale-${i.id}`} onClick={() => setVerificato(i.id, true)}>
                          <ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Imposta verificato
                        </Button>
                      )}
                      {i.can_unverify && (
                        <Button variant="outline" size="sm" data-testid={`annulla-verifica-${i.id}`} onClick={() => setVerificato(i.id, false)}>
                          <Undo2 className="mr-1.5 h-3.5 w-3.5" /> Annulla verifica
                        </Button>
                      )}
                      {i.can_edit && (
                        <Button variant="outline" size="sm" data-testid={`edit-verbale-${i.id}`} onClick={() => openEdit(i)}>
                          <Pencil className="mr-1.5 h-3.5 w-3.5" /> Modifica
                        </Button>
                      )}
                      {i.can_delete && (
                        <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" data-testid={`delete-verbale-${i.id}`} onClick={() => setDelId(i.id)}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!i.can_edit && i.is_creatore && i.verificato && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400" data-testid={`verbale-bloccato-${i.id}`}><Lock className="h-3 w-3" /> Solo consultazione</span>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
